// Comprehensive Glossary of Research Terms (50+ definitions)
export const glossaryTerms = {
  // Philosophical Dimensions
  ontology: {
    term: "Ontology",
    category: "Philosophical Dimension",
    definition: "The branch of philosophy concerned with the nature of reality and existence. In research, ontology asks: 'What is real?' and 'What is the nature of the social world?' Your ontological position determines whether you see reality as objective (existing independently of perception) or subjective (constructed through human interpretation).",
    example: "A realist ontology assumes poverty exists as an objective condition that can be measured, while a relativist ontology views poverty as a socially constructed concept that varies across cultures."
  },

  epistemology: {
    term: "Epistemology",
    category: "Philosophical Dimension",
    definition: "The branch of philosophy concerned with the nature, sources, and limits of knowledge. In research, epistemology asks: 'What can we know?' and 'How can we acquire valid knowledge?' Your epistemological position shapes what methods you consider legitimate for generating knowledge.",
    example: "An objectivist epistemology requires empirical measurement and statistical analysis, while a subjectivist epistemology values in-depth interviews and interpretation of meanings."
  },

  axiology: {
    term: "Axiology",
    category: "Philosophical Dimension",
    definition: "The branch of philosophy concerned with values and ethics. In research, axiology asks: 'What role do values play in research?' Your axiological position determines whether you aim for value-free research, acknowledge values as inherent, or explicitly pursue value-driven research goals.",
    example: "A value-free axiological stance strives for researcher neutrality, while a value-driven stance explicitly pursues social justice outcomes."
  },

  realism: {
    term: "Realism (Ontology)",
    category: "Philosophical Dimension",
    definition: "The ontological position that reality exists independently of human perception and consciousness. There is one true reality 'out there' waiting to be discovered through systematic observation and measurement.",
    example: "A realist researcher studying climate change assumes that global temperatures exist and can be objectively measured, regardless of human beliefs about climate."
  },

  relativism: {
    term: "Relativism (Ontology)",
    category: "Philosophical Dimension",
    definition: "The ontological position that multiple realities exist, constructed through social interaction and individual interpretation. What is 'real' depends on perspective, culture, and context.",
    example: "A relativist researcher studying 'success' would recognize that success means different things to different people and cultures, with no single objective definition."
  },

  objectivism: {
    term: "Objectivism (Epistemology)",
    category: "Philosophical Dimension",
    definition: "The epistemological position that knowledge comes from direct observation of objective reality. Facts are separate from values, and researchers should maintain a detached, neutral stance to produce unbiased findings.",
    example: "An objectivist researcher uses standardized surveys and statistical analysis to minimize researcher influence on findings."
  },

  subjectivism: {
    term: "Subjectivism (Epistemology)",
    category: "Philosophical Dimension",
    definition: "The epistemological position that knowledge is constructed through interaction between researcher and participants. Understanding comes from interpreting meanings, experiences, and perspectives rather than measuring objective facts.",
    example: "A subjectivist researcher uses open-ended interviews to understand how participants make sense of their experiences."
  },

  // Philosophies & Worldviews
  positivism: {
    term: "Positivism",
    category: "Philosophy",
    definition: "A philosophical stance that emphasizes observable, measurable phenomena and the use of scientific methods. Positivists believe in objective reality that can be discovered through empirical testing and quantitative methods.",
    example: "A researcher conducting a controlled laboratory experiment to test the effect of a drug on blood pressure"
  },
  
  interpretivism: {
    term: "Interpretivism",
    category: "Philosophy",
    definition: "A philosophical approach emphasizing that reality is socially constructed and multiple interpretations exist. Focuses on understanding meanings people attach to their experiences through qualitative methods.",
    example: "A researcher conducting in-depth interviews to understand how patients experience chronic illness"
  },
  
  pragmatism: {
    term: "Pragmatism",
    category: "Philosophy",
    definition: "A philosophical stance that values practical consequences and 'what works' over theoretical debates. Pragmatists freely combine quantitative and qualitative methods based on research questions.",
    example: "A researcher using surveys to identify patterns, then conducting interviews to understand why those patterns exist"
  },
  
  criticalRealism: {
    term: "Critical Realism",
    category: "Philosophy",
    definition: "A philosophy recognizing that reality exists independently of our perceptions, but our knowledge of it is socially conditioned. Combines elements of both positivism and interpretivism.",
    example: "A researcher studying poverty by examining both statistical data and lived experiences of people in poverty"
  },
  
  transformative: {
    term: "Transformative Worldview",
    category: "Philosophy",
    definition: "A research paradigm focused on addressing power imbalances, social justice, and advocating for marginalized groups. Research is explicitly political and aims to create social change.",
    example: "A participatory action research project empowering disabled individuals to advocate for policy changes"
  },
  
  // Theory Approaches
  deductive: {
    term: "Deductive Approach",
    category: "Theory Development",
    definition: "A 'top-down' approach where researchers start with a theory or hypothesis, then collect data to test it. Moves from general principles to specific observations.",
    example: "Testing the hypothesis 'increased study time improves exam scores' by measuring both variables in a sample of students"
  },
  
  inductive: {
    term: "Inductive Approach",
    category: "Theory Development",
    definition: "A 'bottom-up' approach where researchers collect data first, then identify patterns to develop theory. Moves from specific observations to broader generalizations.",
    example: "Observing classroom interactions and developing a theory about effective teaching behaviors from the patterns observed"
  },
  
  abductive: {
    term: "Abductive Approach",
    category: "Theory Development",
    definition: "An iterative approach that moves back and forth between theory and data. Researchers identify surprising patterns in data, then seek existing theories to explain them, or develop new ones.",
    example: "Finding unexpected customer behaviors in sales data, exploring theoretical explanations, then collecting more data to refine understanding"
  },
  
  // Methodological Choices
  qualitative: {
    term: "Qualitative Research",
    category: "Methodology",
    definition: "Research focused on exploring meanings, experiences, and social processes. Uses non-numerical data like interviews, observations, and documents. Seeks depth over breadth.",
    example: "Conducting focus groups to understand consumer attitudes toward a new product"
  },
  
  quantitative: {
    term: "Quantitative Research",
    category: "Methodology",
    definition: "Research focused on measuring variables and testing relationships between them using numerical data and statistical analysis. Seeks breadth and generalizability.",
    example: "Surveying 500 customers to measure satisfaction scores and identify factors that predict loyalty"
  },
  
  mixedMethods: {
    term: "Mixed Methods",
    category: "Methodology",
    definition: "Research that intentionally integrates both qualitative and quantitative methods in a single study or program. Provides complementary insights that neither method alone could achieve.",
    example: "Surveying 1000 employees about workplace stress (quant), then interviewing 20 to understand coping strategies (qual)"
  },
  
  multiMethod: {
    term: "Multi-Method Research",
    category: "Methodology",
    definition: "Using multiple methods from the same paradigm (e.g., two qualitative methods or two quantitative methods) without integrating across paradigms.",
    example: "Combining surveys and experiments (both quantitative) to study consumer behavior"
  },
  
  // Research Strategies
  experiment: {
    term: "Experimental Design",
    category: "Strategy",
    definition: "A research strategy where the researcher manipulates one or more independent variables to observe effects on dependent variables, while controlling confounding factors. Gold standard for causal inference.",
    example: "Randomly assigning patients to receive either a new treatment or placebo to test treatment effectiveness"
  },
  
  survey: {
    term: "Survey Research",
    category: "Strategy",
    definition: "A strategy for collecting standardized data from a large number of participants, typically using questionnaires. Efficient for describing populations and testing associations.",
    example: "Distributing an online questionnaire to 5,000 university students about study habits and academic performance"
  },
  
  caseStudy: {
    term: "Case Study",
    category: "Strategy",
    definition: "In-depth investigation of a single case or small number of cases in their real-world context. Provides rich detail but limited generalizability.",
    example: "Studying how one hospital successfully implemented a new electronic health records system"
  },
  
  ethnography: {
    term: "Ethnography",
    category: "Strategy",
    definition: "Immersive study of a cultural group or community in their natural setting over extended time. Researcher becomes a participant-observer to understand shared beliefs and practices.",
    example: "Spending 12 months living with and observing a remote indigenous community to understand their healthcare practices"
  },
  
  groundedTheory: {
    term: "Grounded Theory",
    category: "Strategy",
    definition: "A systematic qualitative approach for generating theory directly from data through iterative coding and constant comparison. Theory is 'grounded' in empirical observations.",
    example: "Interviewing cancer survivors to develop a theory of post-treatment adaptation processes"
  },
  
  phenomenology: {
    term: "Phenomenology",
    category: "Strategy",
    definition: "A qualitative approach focused on understanding the lived experience and essence of a phenomenon from participants' perspectives. Seeks to describe 'what it is like' to experience something.",
    example: "Interviewing mothers who have experienced stillbirth to understand the essence of that grief experience"
  },
  
  actionResearch: {
    term: "Action Research",
    category: "Strategy",
    definition: "A participatory, iterative approach where researchers and practitioners collaborate to solve practical problems while generating knowledge. Emphasizes immediate application and social change.",
    example: "Teachers collaboratively testing and refining new teaching strategies in their classrooms over a school year"
  },
  
  narrativeResearch: {
    term: "Narrative Research",
    category: "Strategy",
    definition: "A qualitative strategy focused on collecting and analyzing stories people tell about their lives. Examines how individuals construct meaning through narrative.",
    example: "Collecting life stories from refugees to understand their resettlement experiences and identity reconstruction"
  },
  
  archivalResearch: {
    term: "Archival Research",
    category: "Strategy",
    definition: "Analysis of existing documents, records, or data sources created for purposes other than research. Efficient but limited by what was recorded.",
    example: "Analyzing historical medical records to track disease patterns over the past century"
  },
  
  // Sampling Methods
  probabilitySampling: {
    term: "Probability Sampling",
    category: "Sampling",
    definition: "Sampling methods where every member of the population has a known, non-zero chance of selection. Enables statistical generalization to the population.",
    example: "Simple random sampling, stratified sampling, cluster sampling"
  },
  
  purposiveSampling: {
    term: "Purposive Sampling",
    category: "Sampling",
    definition: "Non-probability sampling where participants are deliberately selected based on specific characteristics relevant to research questions. Common in qualitative research.",
    example: "Selecting participants who have experienced a particular health condition"
  },
  
  snowballSampling: {
    term: "Snowball Sampling",
    category: "Sampling",
    definition: "A non-probability technique where initial participants recruit additional participants from their networks. Useful for hard-to-reach populations.",
    example: "Asking drug users to refer other drug users for a study on addiction treatment"
  },
  
  convenienceSampling: {
    term: "Convenience Sampling",
    category: "Sampling",
    definition: "Selecting participants based on easy accessibility and availability. Quick and inexpensive but high risk of bias.",
    example: "Surveying students in your own university classes"
  },
  
  theoreticalSampling: {
    term: "Theoretical Sampling",
    category: "Sampling",
    definition: "An iterative sampling approach used in grounded theory where data collection decisions are guided by emerging theoretical insights.",
    example: "After interviewing teachers, deciding to interview principals because the emerging theory suggests their role is important"
  },
  
  // Time Horizons
  crossSectional: {
    term: "Cross-Sectional Study",
    category: "Time Horizon",
    definition: "Research conducted at a single point in time, providing a 'snapshot' of variables and relationships. Efficient but cannot establish temporal precedence.",
    example: "Measuring job satisfaction across a company in one month"
  },
  
  longitudinal: {
    term: "Longitudinal Study",
    category: "Time Horizon",
    definition: "Research conducted over an extended period with multiple data collection points. Can track changes over time and establish temporal precedence.",
    example: "Measuring patient health outcomes at baseline, 6 months, 12 months, and 24 months after treatment"
  },
  
  retrospective: {
    term: "Retrospective Study",
    category: "Time Horizon",
    definition: "Research that looks backward in time, using existing records or participants' memories to reconstruct past events.",
    example: "Reviewing medical charts from the past 10 years to identify risk factors for a disease"
  },
  
  // Data Collection
  structuredInterview: {
    term: "Structured Interview",
    category: "Data Collection",
    definition: "Interviews with predetermined questions asked in a fixed order. All participants answer the same questions, enabling systematic comparison.",
    example: "Using a standardized script to interview all job candidates"
  },
  
  semiStructuredInterview: {
    term: "Semi-Structured Interview",
    category: "Data Collection",
    definition: "Interviews with a list of topics or open-ended questions, but with flexibility to explore emerging themes. Balances consistency and flexibility.",
    example: "Having key questions about patient experience but allowing follow-up probes based on responses"
  },
  
  participantObservation: {
    term: "Participant Observation",
    category: "Data Collection",
    definition: "A technique where the researcher actively engages in the setting being studied while observing behaviors, interactions, and contexts.",
    example: "A researcher working as a volunteer in a homeless shelter while observing social dynamics"
  },
  
  focusGroup: {
    term: "Focus Group",
    category: "Data Collection",
    definition: "A moderated group discussion (typically 6-10 participants) to explore perceptions, opinions, and attitudes about a topic. Group interaction generates rich data.",
    example: "Gathering 8 parents to discuss their perceptions of school policies"
  },

  structuredSurvey: {
    term: "Structured Surveys/Questionnaires",
    category: "Data Collection",
    definition: "Standardized data collection instruments with fixed questions and predetermined response options. All participants answer identical questions in the same order, enabling systematic comparison and statistical analysis.",
    example: "A 50-item Likert scale survey measuring customer satisfaction with rating options from 1 (strongly disagree) to 5 (strongly agree)"
  },

  experiments: {
    term: "Experimental Procedures",
    category: "Data Collection",
    definition: "Controlled data collection where researchers manipulate one or more variables while measuring outcomes. Includes random assignment and control groups to establish cause-and-effect relationships.",
    example: "Randomly assigning students to different study conditions and measuring their test performance afterward"
  },

  structuredObservation: {
    term: "Structured Observation",
    category: "Data Collection",
    definition: "Systematic observation using a predetermined coding scheme to record specific behaviors or events. Produces quantifiable data through standardized observation protocols.",
    example: "Using a checklist to count how many times students raise their hands during class periods"
  },

  secondaryData: {
    term: "Secondary/Administrative Data",
    category: "Data Collection",
    definition: "Using existing data that was originally collected for other purposes. Includes government statistics, organizational records, historical archives, and previously collected research datasets.",
    example: "Analyzing hospital admission records to study healthcare utilization patterns"
  },

  standardizedTests: {
    term: "Standardized Tests/Scales",
    category: "Data Collection",
    definition: "Validated measurement instruments with established reliability and validity. Includes psychological assessments, achievement tests, and diagnostic tools with normative data.",
    example: "Using the Beck Depression Inventory (BDI) to measure depression symptoms in clinical research"
  },

  documentAnalysis: {
    term: "Document Analysis",
    category: "Data Collection",
    definition: "Systematic examination of written, visual, or digital materials. Involves analyzing texts, records, reports, letters, photographs, or other artifacts to understand meaning and context.",
    example: "Analyzing company memos and policy documents to understand organizational culture change"
  },

  fieldNotes: {
    term: "Field Notes/Journals",
    category: "Data Collection",
    definition: "Written records made by researchers during or after observations. Include descriptive notes about what was observed and reflective notes about the researcher's interpretations and reactions.",
    example: "A researcher recording detailed observations and personal reflections each day during a six-month ethnographic study"
  },

  visualMethods: {
    term: "Visual Methods",
    category: "Data Collection",
    definition: "Data collection using visual materials including photography, video recording, drawings, and diagrams. Can be researcher-generated or participant-generated to capture experiences that words may not fully express.",
    example: "Asking participants to take photos of their daily routines and then discussing the images in interviews"
  },
  
  // Validity & Quality
  internalValidity: {
    term: "Internal Validity",
    category: "Quality Criteria",
    definition: "The degree to which a study can establish causal relationships free from confounding factors. Can we trust that X caused Y?",
    example: "Strong internal validity: randomized controlled trial with proper controls. Weak: observational study with many confounds."
  },
  
  externalValidity: {
    term: "External Validity",
    category: "Quality Criteria",
    definition: "The extent to which research findings can be generalized to other populations, settings, or times beyond the study sample.",
    example: "Strong external validity: representative national sample. Weak: college student convenience sample."
  },
  
  reliability: {
    term: "Reliability",
    category: "Quality Criteria",
    definition: "The consistency and repeatability of research measurements or findings. Would the same methods produce similar results if repeated?",
    example: "A scale that gives the same weight measurement when you step on it multiple times"
  },
  
  credibility: {
    term: "Credibility",
    category: "Quality Criteria",
    definition: "The qualitative equivalent of internal validity. Do the findings accurately represent participants' realities? Established through prolonged engagement, triangulation, member checking.",
    example: "Sharing interview transcripts with participants to verify your interpretation matches their intended meaning"
  },
  
  transferability: {
    term: "Transferability",
    category: "Quality Criteria",
    definition: "The qualitative equivalent of external validity. Can readers determine if findings are applicable to their own contexts? Requires thick description.",
    example: "Providing rich detail about participants, setting, and context so readers can judge relevance to their situation"
  },
  
  confirmability: {
    term: "Confirmability",
    category: "Quality Criteria",
    definition: "The degree to which qualitative findings are shaped by participants' responses rather than researcher bias. Demonstrated through audit trails and reflexivity.",
    example: "Keeping detailed memos about analytical decisions and how personal biases might influence interpretation"
  },

  constructValidity: {
    term: "Construct Validity",
    category: "Quality Criteria",
    definition: "The extent to which a measurement tool actually measures the theoretical concept it claims to measure. Ensures that operational definitions accurately represent abstract constructs.",
    example: "Demonstrating that an 'anxiety scale' actually measures anxiety and not depression or stress through correlation with other validated anxiety measures"
  },

  statConclusionValidity: {
    term: "Statistical Conclusion Validity",
    category: "Quality Criteria",
    definition: "The degree to which conclusions about relationships between variables are accurate based on statistical analysis. Concerns adequate sample size, appropriate tests, and correct interpretation of results.",
    example: "Ensuring sufficient statistical power (e.g., N=200) to detect meaningful effects and using the correct statistical test for the data type"
  },

  dependability: {
    term: "Dependability",
    category: "Quality Criteria",
    definition: "The qualitative equivalent of reliability. Refers to whether findings would be consistent if the study were replicated with similar participants in similar contexts. Established through detailed documentation of research processes.",
    example: "Maintaining a detailed audit trail of all research decisions so another researcher could follow the same process"
  },

  authenticity: {
    term: "Authenticity",
    category: "Quality Criteria",
    definition: "The degree to which research fairly and completely represents the range of different perspectives among participants. Includes giving voice to all viewpoints, including marginalized ones.",
    example: "Ensuring that minority viewpoints within the participant group are represented in the findings, not just the dominant perspective"
  },

  resonance: {
    term: "Resonance",
    category: "Quality Criteria",
    definition: "The extent to which research findings resonate with or ring true to participants and readers with similar experiences. Good qualitative research should evoke recognition and deeper understanding.",
    example: "Participants reading the findings say 'Yes, that's exactly what it's like' - the research captures and communicates their experience authentically"
  },
  
  // Analysis
  thematicAnalysis: {
    term: "Thematic Analysis",
    category: "Qualitative Analysis",
    definition: "A flexible method for identifying, analyzing, and reporting patterns (themes) across qualitative data. Widely used across disciplines.",
    example: "Coding interview transcripts and identifying recurring themes like 'work-life balance challenges' or 'inadequate manager support'"
  },
  
  contentAnalysis: {
    term: "Content Analysis",
    category: "Qualitative Analysis",
    definition: "Systematic analysis of text or media content to identify patterns, themes, or frequencies. Can be qualitative or quantitative.",
    example: "Coding newspaper articles to count mentions of climate change and categorize framing (threat vs. opportunity)"
  },
  
  discourseAnalysis: {
    term: "Discourse Analysis",
    category: "Qualitative Analysis",
    definition: "Analysis of language use in social contexts to understand how meaning is constructed, power is exercised, and identities are formed.",
    example: "Examining political speeches to understand how politicians construct national identity"
  },
  
  descriptiveStatistics: {
    term: "Descriptive Statistics",
    category: "Quantitative Analysis",
    definition: "Statistical methods for summarizing and describing data distributions using measures like means, medians, standard deviations, and frequencies.",
    example: "Reporting that the average age of participants was 42.5 years (SD = 8.3)"
  },
  
  inferentialStatistics: {
    term: "Inferential Statistics",
    category: "Quantitative Analysis",
    definition: "Statistical methods for making inferences about populations based on sample data. Includes hypothesis testing and confidence intervals.",
    example: "Using a t-test to determine if the difference in test scores between two groups is statistically significant"
  },
  
  regression: {
    term: "Regression Analysis",
    category: "Quantitative Analysis",
    definition: "Statistical techniques for modeling relationships between a dependent variable and one or more independent variables. Used for prediction and understanding relationships.",
    example: "Predicting house prices based on square footage, number of bedrooms, and neighborhood"
  },

  anova: {
    term: "ANOVA/ANCOVA",
    category: "Quantitative Analysis",
    definition: "Analysis of Variance (ANOVA) tests whether means differ across three or more groups. ANCOVA adds control for covariates. Both help determine if group differences are statistically significant.",
    example: "Comparing average test scores across three different teaching methods to see which is most effective"
  },

  factorAnalysis: {
    term: "Factor Analysis",
    category: "Quantitative Analysis",
    definition: "A technique for identifying underlying constructs (factors) that explain correlations among observed variables. Exploratory FA discovers factors; Confirmatory FA tests hypothesized structures.",
    example: "Analyzing responses to 30 personality items to identify 5 underlying personality dimensions"
  },

  sem: {
    term: "Structural Equation Modeling (SEM)",
    category: "Quantitative Analysis",
    definition: "Advanced statistical method combining factor analysis and path analysis to test complex relationships among observed and latent (unmeasured) variables simultaneously.",
    example: "Testing a theoretical model where customer satisfaction (latent) predicts loyalty through trust and commitment"
  },

  timeSeries: {
    term: "Time Series Analysis",
    category: "Quantitative Analysis",
    definition: "Statistical methods for analyzing data collected over time to identify trends, seasonal patterns, and forecast future values. Accounts for temporal dependencies in data.",
    example: "Analyzing monthly sales data over 5 years to forecast next year's revenue"
  },

  groundedTheoryCoding: {
    term: "Grounded Theory Coding",
    category: "Qualitative Analysis",
    definition: "A systematic coding approach with three stages: Open coding (initial labels), Axial coding (connecting categories), and Selective coding (identifying core category). Generates theory from data.",
    example: "Starting with 200+ initial codes from interviews, refining to 15 categories, then identifying one core process explaining the phenomenon"
  },

  narrativeAnalysis: {
    term: "Narrative Analysis",
    category: "Qualitative Analysis",
    definition: "Analysis focusing on stories people tell, examining plot structure, characters, turning points, and how meaning is constructed through storytelling.",
    example: "Analyzing how cancer survivors structure their illness stories with beginning (diagnosis), middle (treatment), and resolution (survivorship)"
  },

  phenomenologicalAnalysis: {
    term: "Phenomenological Analysis",
    category: "Qualitative Analysis",
    definition: "Analysis aimed at describing the essential structure of a lived experience. Involves bracketing researcher assumptions and identifying invariant meanings across participants.",
    example: "Identifying the core elements that make up the experience of being a first-generation college student"
  },

  frameworkAnalysis: {
    term: "Framework Analysis",
    category: "Qualitative Analysis",
    definition: "A matrix-based approach to qualitative analysis where data is organized in rows (cases) and columns (themes). Enables systematic comparison across cases and themes.",
    example: "Creating a chart with 20 participants as rows and 8 themes as columns, summarizing each cell to compare patterns"
  },

  ipa: {
    term: "Interpretative Phenomenological Analysis (IPA)",
    category: "Qualitative Analysis",
    definition: "A qualitative approach focusing on how individuals make sense of significant life experiences. Combines phenomenology (lived experience) with hermeneutics (interpretation).",
    example: "Exploring how 6 individuals with chronic pain interpret and make meaning of their condition in daily life"
  },
  
  // Research Ethics
  informedConsent: {
    term: "Informed Consent",
    category: "Ethics",
    definition: "The ethical principle that participants must voluntarily agree to participate in research after being fully informed about procedures, risks, benefits, and their right to withdraw.",
    example: "Providing a written consent form explaining study procedures and having participants sign before data collection"
  },
  
  confidentiality: {
    term: "Confidentiality",
    category: "Ethics",
    definition: "The ethical obligation to protect participants' private information and not disclose it to unauthorized individuals. Typically involves removing identifying information from data.",
    example: "Storing interview recordings with ID codes rather than names, keeping the key in a separate locked location"
  },
  
  anonymity: {
    term: "Anonymity",
    category: "Ethics",
    definition: "Ensuring that participants' identities cannot be determined, even by the researcher. Stronger than confidentiality but not always possible.",
    example: "Online survey where no identifying information (email, IP address) is collected"
  },
  
  // Research Design
  independentVariable: {
    term: "Independent Variable (IV)",
    category: "Research Design",
    definition: "The variable that is manipulated or categorized by the researcher to observe its effect on the dependent variable. The presumed cause in a causal relationship.",
    example: "In a study of teaching methods on learning, teaching method is the IV"
  },
  
  dependentVariable: {
    term: "Dependent Variable (DV)",
    category: "Research Design",
    definition: "The outcome variable that is measured to assess the effect of the independent variable. The presumed effect in a causal relationship.",
    example: "In a study of teaching methods on learning, test scores are the DV"
  },
  
  confoundingVariable: {
    term: "Confounding Variable",
    category: "Research Design",
    definition: "An extraneous variable that is correlated with both the independent and dependent variables, potentially creating a spurious relationship.",
    example: "When studying exercise and heart health, age is a confounder because it affects both exercise levels and heart disease risk"
  },
  
  operationalization: {
    term: "Operationalization",
    category: "Research Design",
    definition: "The process of defining how abstract concepts will be measured in concrete, observable terms. Translating theory into measurable variables.",
    example: "Defining 'job satisfaction' as scores on a standardized 20-item questionnaire"
  },
  
  triangulation: {
    term: "Triangulation",
    category: "Research Design",
    definition: "Using multiple methods, data sources, investigators, or theories to cross-check findings and enhance validity and credibility.",
    example: "Combining survey data, interviews, and document analysis to study organizational culture"
  },
  
  saturation: {
    term: "Data Saturation",
    category: "Qualitative Research",
    definition: "The point in qualitative data collection when no new information or themes are emerging. Used to determine when to stop collecting data.",
    example: "After interviewing 15 participants, the researcher finds that subsequent interviews yield no new insights"
  }
};

// Helper function to get term by key
export const getGlossaryTerm = (key) => {
  return glossaryTerms[key] || null;
};

// Helper function to search glossary
export const searchGlossary = (searchTerm) => {
  const lowerSearch = searchTerm.toLowerCase();
  return Object.entries(glossaryTerms)
    .filter(([key, term]) => 
      term.term.toLowerCase().includes(lowerSearch) ||
      term.definition.toLowerCase().includes(lowerSearch) ||
      term.category.toLowerCase().includes(lowerSearch)
    )
    .map(([key, term]) => ({ key, ...term }));
};

// Get all terms in a category
export const getTermsByCategory = (category) => {
  return Object.entries(glossaryTerms)
    .filter(([key, term]) => term.category === category)
    .map(([key, term]) => ({ key, ...term }));
};

export default glossaryTerms;
